import { CountryCode, countries } from "@redotech/locale/countries";
import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import LightningIcon from "@redotech/redo-icons/lightning-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { ExternalReturnHubType } from "@redotech/redo-model/team";
import {
  AddressCore,
  AddressVerificationModal,
} from "@redotech/redo-web/address-verification/address-verification-modal";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { Text } from "@redotech/redo-web/text";
import type { InputProvider } from "@redotech/ui/form";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { automationUrl } from "../../../merchant-app";
import type { availableReturnsCentersInput } from "./redo-managed-returns-settings";

export interface ReturnsCenterConfigFormValue {
  returnsCenterId: string;
  consolidationScheduleDays: string;
  shipToName: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  refulfillment: boolean;
}

type AvailableReturnsCentersForm = InputProvider.Form<
  typeof availableReturnsCentersInput
>;

interface ReturnsCenterRowData {
  id: string;
  label: string;
  schedule: string;
  addressSummary: string;
  provider: string;
  locationId: string;
  ownerTeamName: string;
  refulfillment: boolean;
  hasLinkedLocation: boolean;
  automationId: string | undefined;
}

const HUB_DISPLAY_NAMES: Record<ExternalReturnHubType, string> = {
  [ExternalReturnHubType.RETURNBEAR]: "ReturnBear",
  [ExternalReturnHubType.BLUEYONDER]: "Blue Yonder",
  [ExternalReturnHubType.SHIPMONK]: "ShipMonk",
  [ExternalReturnHubType.RYDER]: "Ryder",
  [ExternalReturnHubType.DEPOSCO]: "Deposco",
  [ExternalReturnHubType.NETSUITE]: "NetSuite",
};

const EXTERNAL_HUB_OPTIONS: RedoListItem<string>[] = [
  { id: "", type: "text" as const, text: "None", value: "" },
  ...Object.values(ExternalReturnHubType).map((hub) => ({
    id: hub,
    type: "text" as const,
    text: HUB_DISPLAY_NAMES[hub],
    value: hub,
  })),
];

function summarizeAddress(config: ReturnsCenterConfigFormValue): string {
  const parts = [config.shipToCity, config.shipToState, config.shipToCountry]
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
}

function formatSchedule(days: string): string {
  const trimmed = days.trim();
  if (!trimmed) return "Not scheduled";
  const n = parseInt(trimmed, 10);
  if (isNaN(n)) return trimmed;
  if (n === 1) return "Every day";
  return `Every ${n} days`;
}

function buildDetailLine(row: ReturnsCenterRowData): string {
  const parts: string[] = [];
  if (row.ownerTeamName) {
    parts.push(row.ownerTeamName);
  }
  if (row.schedule !== "Not scheduled") {
    parts.push(row.schedule);
  }
  if (row.addressSummary) {
    parts.push(row.addressSummary);
  }
  const providerName = row.provider
    ? (HUB_DISPLAY_NAMES[row.provider as ExternalReturnHubType] ?? row.provider)
    : "";
  if (providerName) {
    parts.push(providerName);
  }
  return parts.join(" · ");
}

const EMPTY_CONFIG: Omit<ReturnsCenterConfigFormValue, "returnsCenterId"> = {
  consolidationScheduleDays: "",
  shipToName: "",
  shipToStreet1: "",
  shipToStreet2: "",
  shipToCity: "",
  shipToState: "",
  shipToZip: "",
  shipToCountry: "",
  refulfillment: false,
};

function centerLabel(center: {
  name: string;
  city: string | null;
  state: string | null;
}): string {
  return `${center.name}${center.city ? ` - ${center.city}` : ""}${center.state ? `, ${center.state}` : ""}`;
}

const ROW_BORDER: React.CSSProperties = {
  borderBottom: "1px solid var(--redo-colors-border-border-secondary)",
};

const ROW_MIN_HEIGHT: React.CSSProperties = { minHeight: 56 };

const ReturnsCenterRow = memo(function ReturnsCenterRow({
  row,
  onEdit,
  onRemove,
  onToggleRefulfillment,
  disabled,
  teamId,
  isLast,
}: {
  row: ReturnsCenterRowData;
  onEdit: (centerId: string) => void;
  onRemove: (centerId: string) => void;
  onToggleRefulfillment: (centerId: string, value: boolean) => void;
  disabled: boolean;
  teamId: string;
  isLast: boolean;
}) {
  const detailLine = buildDetailLine(row);

  const handleToggle = useCallback(
    (value: boolean) => {
      onToggleRefulfillment(row.id, value);
    },
    [onToggleRefulfillment, row.id],
  );

  return (
    <Flex
      align="center"
      cursor="pointer"
      dir="row"
      gap="lg"
      hoverColor="secondary"
      justify="space-between"
      onClick={() => onEdit(row.id)}
      p="lg"
      style={isLast ? ROW_MIN_HEIGHT : { ...ROW_BORDER, ...ROW_MIN_HEIGHT }}
    >
      <Flex dir="column" gap="xxs" grow="1">
        <Text fontSize="xs" fontWeight="medium">
          {row.label}
        </Text>
        {detailLine && (
          <Text fontSize="xxs" textColor="tertiary">
            {detailLine}
          </Text>
        )}
      </Flex>
      {row.hasLinkedLocation && (
        <Flex onClick={(e: React.MouseEvent) => e.stopPropagation()} shrink={0}>
          <RedoToggle
            disabled={disabled}
            setValue={handleToggle}
            size="sm"
            value={row.refulfillment}
          />
        </Flex>
      )}
      <Flex
        dir="row"
        gap="xs"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        shrink={0}
      >
        {row.automationId && (
          <a
            href={automationUrl(teamId, row.automationId)}
            rel="noreferrer"
            target="_blank"
          >
            <RedoButton
              hierarchy="tertiary"
              IconLeading={LightningIcon}
              size="xs"
            />
          </a>
        )}
        <RedoButton
          disabled={disabled}
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={() => onRemove(row.id)}
          size="xs"
        />
      </Flex>
    </Flex>
  );
});

export const AvailableReturnsCentersTable = memo(
  function AvailableReturnsCentersTable({
    input,
    disabled,
    hubMappings,
    onHubMappingsChange,
    teamId,
  }: {
    input: AvailableReturnsCentersForm;
    disabled: boolean;
    hubMappings: Record<string, { provider: string; locationId: string }>;
    onHubMappingsChange: (
      mappings: Record<string, { provider: string; locationId: string }>,
    ) => void;
    teamId: string;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

    const { value: returnsCentersData, pending: returnsCentersPending } =
      useRpcData(rpcClient, "getReturnsCenters", { input: { sort: null } });

    const { value: automationsData } = useRpcData(
      rpcClient,
      "getRmrAutomations",
      { input: { teamId } },
    );

    const automationIdByThirdPartyAccountId = useMemo(() => {
      const map = new Map<string, string>();
      for (const {
        thirdPartyAccountId,
        automationId,
      } of automationsData?.automations ?? []) {
        map.set(thirdPartyAccountId, automationId);
      }
      return map;
    }, [automationsData]);
    const allReturnsCenters = useMemo(
      () => returnsCentersData?.returnsCenters ?? [],
      [returnsCentersData],
    );

    const configs = input.value;

    const [editingCenterId, setEditingCenterId] = useState<string | null>(null);
    const [editDraft, setEditDraft] =
      useState<ReturnsCenterConfigFormValue | null>(null);
    const [editHubProvider, setEditHubProvider] = useState("");
    const [editHubLocationId, setEditHubLocationId] = useState("");

    const openEditModal = useCallback(
      (centerId: string) => {
        const existing = configs.find((c) => c.returnsCenterId === centerId);
        setEditDraft(
          existing ?? { returnsCenterId: centerId, ...EMPTY_CONFIG },
        );
        const mapping = hubMappings[centerId];
        setEditHubProvider(mapping?.provider ?? "");
        setEditHubLocationId(mapping?.locationId ?? "");
        setEditingCenterId(centerId);
      },
      [configs, hubMappings],
    );

    const closeEditModal = useCallback(() => {
      setEditingCenterId(null);
      setEditDraft(null);
      setEditHubProvider("");
      setEditHubLocationId("");
    }, []);

    const saveEditModal = useCallback(() => {
      if (!editDraft) return;
      input.setValue(
        configs.map((c) =>
          c.returnsCenterId === editDraft.returnsCenterId ? editDraft : c,
        ),
      );
      const centerId = editDraft.returnsCenterId;
      const newMappings = { ...hubMappings };
      if (!editHubProvider) {
        delete newMappings[centerId];
      } else {
        newMappings[centerId] = {
          provider: editHubProvider,
          locationId: editHubLocationId,
        };
      }
      onHubMappingsChange(newMappings);
      closeEditModal();
    }, [
      editDraft,
      configs,
      input,
      hubMappings,
      editHubProvider,
      editHubLocationId,
      onHubMappingsChange,
      closeEditModal,
    ]);

    const removeCenter = useCallback(
      (centerId: string) => {
        input.setValue(configs.filter((c) => c.returnsCenterId !== centerId));
      },
      [configs, input],
    );

    const toggleRefulfillment = useCallback(
      (centerId: string, value: boolean) => {
        input.setValue(
          configs.map((c) =>
            c.returnsCenterId === centerId ? { ...c, refulfillment: value } : c,
          ),
        );
      },
      [configs, input],
    );

    const addCenter = useCallback(
      (centerId: string) => {
        input.setValue([
          ...configs,
          { returnsCenterId: centerId, ...EMPTY_CONFIG },
        ]);
      },
      [configs, input],
    );

    const selectedCenterIds = useMemo(
      () => new Set(configs.map((c) => c.returnsCenterId)),
      [configs],
    );

    const rowsOfData: ReturnsCenterRowData[] = useMemo(() => {
      return configs.map((config) => {
        const center = allReturnsCenters.find(
          (c) => c.id === config.returnsCenterId,
        );
        const mapping = hubMappings[config.returnsCenterId];
        return {
          id: config.returnsCenterId,
          label: center ? centerLabel(center) : config.returnsCenterId,
          schedule: formatSchedule(config.consolidationScheduleDays),
          addressSummary: summarizeAddress(config),
          provider: mapping?.provider ?? "",
          locationId: mapping?.locationId ?? "",
          ownerTeamName: center?.ownerTeamName ?? "",
          refulfillment: config.refulfillment,
          hasLinkedLocation: center?.hasLinkedLocation ?? false,
          automationId: center?.thirdPartyAccountId
            ? automationIdByThirdPartyAccountId.get(center.thirdPartyAccountId)
            : undefined,
        };
      });
    }, [
      allReturnsCenters,
      automationIdByThirdPartyAccountId,
      configs,
      hubMappings,
    ]);

    const addOptions: RedoListItem<string>[] = useMemo(() => {
      return allReturnsCenters
        .filter((c) => !selectedCenterIds.has(c.id))
        .map((center) => ({
          type: "text" as const,
          id: center.id,
          value: center.id,
          text: centerLabel(center),
        }));
    }, [allReturnsCenters, selectedCenterIds]);

    const handleAddSelected = useCallback(
      ({ item }: { item: RedoListItem<string> }) => {
        addCenter(item.value);
      },
      [addCenter],
    );

    if (returnsCentersPending) {
      return (
        <Text fontSize="sm" textColor="tertiary">
          Loading returns centers...
        </Text>
      );
    }

    const editingCenter = editingCenterId
      ? allReturnsCenters.find((c) => c.id === editingCenterId)
      : null;

    const hasAvailableCenters = addOptions.length > 0;

    return (
      <Flex dir="column" gap="none">
        {hasAvailableCenters && (
          <Flex p="lg">
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              options={addOptions}
              optionSelected={handleAddSelected}
              placeholder="Add a returns center..."
              size="xs"
            />
          </Flex>
        )}
        {rowsOfData.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            pb="sm"
            pt="lg"
            px="lg"
            style={ROW_BORDER}
            w="full"
          >
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              Returns Center
            </Text>
            <Text
              fontSize="xs"
              fontWeight="medium"
              style={{ marginRight: 40 }}
              textColor="tertiary"
            >
              Re-fulfillment
            </Text>
          </Flex>
        )}
        <Flex align="stretch" dir="column" gap="none">
          {rowsOfData.map((row, index) => (
            <ReturnsCenterRow
              disabled={disabled}
              isLast={index === rowsOfData.length - 1}
              key={row.id}
              onEdit={openEditModal}
              onRemove={removeCenter}
              onToggleRefulfillment={toggleRefulfillment}
              row={row}
              teamId={teamId}
            />
          ))}
        </Flex>

        {editDraft && editingCenter && (
          <EditReturnsCenterModal
            centerLabel={centerLabel(editingCenter)}
            draft={editDraft}
            hubLocationId={editHubLocationId}
            hubProvider={editHubProvider}
            onClose={closeEditModal}
            onHubLocationIdChange={setEditHubLocationId}
            onHubProviderChange={setEditHubProvider}
            onSave={saveEditModal}
            setDraft={setEditDraft}
          />
        )}
      </Flex>
    );
  },
);

const EditReturnsCenterModal = memo(function EditReturnsCenterModal({
  centerLabel,
  draft,
  setDraft,
  hubProvider,
  hubLocationId,
  onHubProviderChange,
  onHubLocationIdChange,
  onClose,
  onSave,
}: {
  centerLabel: string;
  draft: ReturnsCenterConfigFormValue;
  setDraft: (value: ReturnsCenterConfigFormValue) => void;
  hubProvider: string;
  hubLocationId: string;
  onHubProviderChange: (provider: string) => void;
  onHubLocationIdChange: (locationId: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const validateMutation = useRpcMutation(RedoAdminRpcClientContext, {
    method: "validateReturnsCenterAddress",
  });
  const [addressSuggestion, setAddressSuggestion] = useState<
    AddressCore | undefined
  >();
  const [verificationWarning, setVerificationWarning] = useState(false);

  const updateField = useCallback(
    (field: keyof ReturnsCenterConfigFormValue, value: string) => {
      setDraft({ ...draft, [field]: value });
    },
    [draft, setDraft],
  );

  const handleVerificationClose = useCallback(() => {
    setAddressSuggestion(undefined);
    setVerificationWarning(false);
  }, []);

  const handleVerificationSave = useCallback(
    (chosen: AddressCore) => {
      setAddressSuggestion(undefined);
      setVerificationWarning(false);
      setDraft({
        ...draft,
        shipToStreet1: chosen.street1,
        shipToStreet2: chosen.street2 ?? "",
        shipToCity: chosen.city,
        shipToState: chosen.state ?? "",
        shipToZip: chosen.zip,
        shipToCountry: chosen.country,
      });
      onSave();
    },
    [draft, setDraft, onSave],
  );

  const handleSaveWithValidation = useCallback(async () => {
    const country = draft.shipToCountry;

    // Skip validation if no address or domestic
    if (!country || country === "US" || !draft.shipToStreet1) {
      onSave();
      return;
    }

    try {
      const result = await validateMutation.mutateAsync({
        street1: draft.shipToStreet1 || null,
        street2: draft.shipToStreet2 || null,
        city: draft.shipToCity || null,
        state: draft.shipToState || null,
        postalCode: draft.shipToZip || null,
        country,
      });

      if (!result.isInternational) {
        onSave();
      } else if (!result.success) {
        setVerificationWarning(true);
      } else if (result.proposedAddress) {
        setAddressSuggestion({
          street1: result.proposedAddress.street1,
          street2: result.proposedAddress.street2 ?? undefined,
          city: result.proposedAddress.city,
          state: result.proposedAddress.state ?? undefined,
          zip: result.proposedAddress.postalCode,
          country: result.proposedAddress.country,
        });
      } else {
        onSave();
      }
    } catch {
      // Fail-open: if validation call fails, proceed with save
      onSave();
    }
  }, [draft, onSave, validateMutation]);

  const countryOptions: TextRedoListItem<CountryCode>[] = useMemo(
    () =>
      countries.map((country) => ({
        id: country.code,
        type: "text" as const,
        text: country.name,
        value: country.code,
      })),
    [],
  );

  const [countrySearch, setCountrySearch] = useState("");

  const filteredCountryOptions = useMemo(
    () =>
      countrySearch
        ? countryOptions.filter((o) =>
            o.text.toLowerCase().includes(countrySearch.toLowerCase()),
          )
        : countryOptions,
    [countryOptions, countrySearch],
  );

  const handleCountrySelected = useCallback(
    ({ item }: { item: RedoListItem<CountryCode> }) => {
      setDraft({ ...draft, shipToCountry: item.value ?? "" });
      setCountrySearch("");
    },
    [draft, setDraft],
  );

  const selectedProviderOption = useMemo(
    () =>
      EXTERNAL_HUB_OPTIONS.find((o) => o.value === hubProvider) ??
      EXTERNAL_HUB_OPTIONS[0],
    [hubProvider],
  );

  const handleProviderSelected = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      onHubProviderChange(item.value);
      if (!item.value) {
        onHubLocationIdChange("");
      }
    },
    [onHubProviderChange, onHubLocationIdChange],
  );

  return (
    <>
      <RedoModal
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Save",
          onClick: handleSaveWithValidation,
          disabled: validateMutation.isPending,
          size: "xs",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: onClose,
          disabled: false,
          size: "xs",
        }}
        subtitle="Configure consolidation, ship-to address, and hub provider."
        title={centerLabel}
      >
        <Flex dir="column" gap="lg">
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              Consolidation
            </Text>
            <RedoTextInput
              label="Days between shipments"
              placeholder="e.g. 7"
              setValue={(v) => updateField("consolidationScheduleDays", v)}
              value={draft.consolidationScheduleDays}
            />
          </Flex>
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              Ship-To Address
            </Text>
            <RedoTextInput
              label="Name"
              setValue={(v) => updateField("shipToName", v)}
              value={draft.shipToName}
            />
            <RedoTextInput
              label="Street 1"
              setValue={(v) => updateField("shipToStreet1", v)}
              value={draft.shipToStreet1}
            />
            <RedoTextInput
              label="Street 2"
              setValue={(v) => updateField("shipToStreet2", v)}
              value={draft.shipToStreet2}
            />
            <Flex dir="row" gap="sm" w="full">
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <RedoTextInput
                  label="City"
                  setValue={(v) => updateField("shipToCity", v)}
                  value={draft.shipToCity}
                />
              </div>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <RedoTextInput
                  label="State"
                  setValue={(v) => updateField("shipToState", v)}
                  value={draft.shipToState}
                />
              </div>
            </Flex>
            <Flex dir="row" gap="sm" w="full">
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <RedoTextInput
                  label="ZIP"
                  setValue={(v) => updateField("shipToZip", v)}
                  value={draft.shipToZip}
                />
              </div>
              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <RedoSingleSelectDropdownInput<CountryCode>
                  label="Country"
                  options={filteredCountryOptions}
                  optionSelected={handleCountrySelected}
                  placeholder="Select country"
                  searchPlaceholder="Search countries..."
                  searchString={countrySearch}
                  selectedItem={countryOptions.find(
                    (option) => option.value === draft.shipToCountry,
                  )}
                  setSearchString={setCountrySearch}
                />
              </div>
            </Flex>
          </Flex>
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              External Hub
            </Text>
            <RedoSingleSelectDropdownInput
              label="Provider"
              options={EXTERNAL_HUB_OPTIONS}
              optionSelected={handleProviderSelected}
              selectedItem={selectedProviderOption}
              size="xs"
            />
            {hubProvider && (
              <RedoTextInput
                label="Location ID"
                placeholder="Enter location ID"
                setValue={onHubLocationIdChange}
                value={hubLocationId}
              />
            )}
          </Flex>
        </Flex>
      </RedoModal>
      {(addressSuggestion || verificationWarning) && (
        <AddressVerificationModal
          address={{
            street1: draft.shipToStreet1,
            street2: draft.shipToStreet2 || undefined,
            city: draft.shipToCity,
            state: draft.shipToState || undefined,
            zip: draft.shipToZip,
            country: draft.shipToCountry,
          }}
          hasVerificationError={verificationWarning}
          onClose={handleVerificationClose}
          onSave={handleVerificationSave}
          suggestion={addressSuggestion}
        />
      )}
    </>
  );
});
